import { useState, useEffect } from 'react';
import { Save, Instagram, MessageCircle, Mail, Globe, Palette, Bell, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import AdminLayout from '@/components/admin/AdminLayout';
import { settingsAPI } from '@/lib/api';
import { toast } from 'sonner';

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    whatsapp_group_link: '',
    whatsapp_channel_link: '',
    auto_redirect_enabled: false,
    auto_redirect_delay: 3,
    site_name: '',
    site_description: '',
    instagram_url: '',
    email_contact: '',
    hero_title: '',
    hero_subtitle: '',
    hero_gradient_start: '#FF6B00',
    hero_gradient_end: '#FFD700',
    urgency_banner_enabled: true,
    urgency_banner_text: '',
    vip_group_link: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsAPI.get();
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Configuracoes salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configuracoes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Configuracoes">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configuracoes">
      <div className="max-w-4xl space-y-6">
        {/* Hero Section Editor */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="w-5 h-5 text-orange-500" />
              Banner Principal (Hero)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Titulo do Banner</Label>
              <Input
                value={settings.hero_title}
                onChange={(e) => handleChange('hero_title', e.target.value)}
                placeholder="Bem-vindo aos Melhores Achadinhos!"
                data-testid="hero-title-input"
              />
            </div>
            <div>
              <Label>Subtitulo</Label>
              <Input
                value={settings.hero_subtitle}
                onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                placeholder="Ofertas imperdiveis para voce!"
                data-testid="hero-subtitle-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cor Gradiente (Inicio)</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.hero_gradient_start}
                    onChange={(e) => handleChange('hero_gradient_start', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    value={settings.hero_gradient_start}
                    onChange={(e) => handleChange('hero_gradient_start', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Cor Gradiente (Fim)</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.hero_gradient_end}
                    onChange={(e) => handleChange('hero_gradient_end', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border"
                  />
                  <Input
                    value={settings.hero_gradient_end}
                    onChange={(e) => handleChange('hero_gradient_end', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            <div className="rounded-xl p-6 text-center text-white font-bold" style={{
              background: `linear-gradient(135deg, ${settings.hero_gradient_start} 0%, ${settings.hero_gradient_end} 100%)`
            }}>
              Preview: {settings.hero_title || 'Titulo'}
            </div>
          </CardContent>
        </Card>

        {/* Urgency Banner */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-500" />
              Banner de Urgencia (Topo)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Ativar Banner de Urgencia</Label>
              <Switch
                checked={settings.urgency_banner_enabled}
                onCheckedChange={(v) => handleChange('urgency_banner_enabled', v)}
                data-testid="urgency-banner-toggle"
              />
            </div>
            <div>
              <Label>Texto do Banner</Label>
              <Input
                value={settings.urgency_banner_text}
                onChange={(e) => handleChange('urgency_banner_text', e.target.value)}
                placeholder="OFERTAS RELAMPAGO EXPIRAM EM POUCOS MINUTOS!"
                data-testid="urgency-banner-text-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              Links Sociais e Contato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> E-mail de Contato</Label>
              <Input
                value={settings.email_contact || ''}
                onChange={(e) => handleChange('email_contact', e.target.value)}
                placeholder="carvalhocomercio7@gmail.com"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2"><Instagram className="w-4 h-4" /> Instagram URL</Label>
              <Input
                value={settings.instagram_url || ''}
                onChange={(e) => handleChange('instagram_url', e.target.value)}
                placeholder="https://www.instagram.com/seuinsta"
                data-testid="instagram-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp Canal</Label>
              <Input
                value={settings.whatsapp_channel_link || ''}
                onChange={(e) => handleChange('whatsapp_channel_link', e.target.value)}
                placeholder="https://whatsapp.com/channel/..."
                data-testid="whatsapp-channel-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2"><LinkIcon className="w-4 h-4" /> WhatsApp Grupo VIP</Label>
              <Input
                value={settings.whatsapp_group_link || ''}
                onChange={(e) => handleChange('whatsapp_group_link', e.target.value)}
                placeholder="https://chat.whatsapp.com/..."
                data-testid="whatsapp-group-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Link Botao VIP (Modal Sucesso)</Label>
              <Input
                value={settings.vip_group_link || ''}
                onChange={(e) => handleChange('vip_group_link', e.target.value)}
                placeholder="https://whatsapp.com/channel/..."
                data-testid="vip-link-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Redirect */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Redirecionamento WhatsApp
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Ativar Redirecionamento Automatico</Label>
              <Switch
                checked={settings.auto_redirect_enabled}
                onCheckedChange={(v) => handleChange('auto_redirect_enabled', v)}
                data-testid="auto-redirect-toggle"
              />
            </div>
            <div>
              <Label>Tempo de Espera (segundos)</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={settings.auto_redirect_delay}
                onChange={(e) => handleChange('auto_redirect_delay', parseInt(e.target.value) || 3)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-6 text-base"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Salvando...</>
          ) : (
            <><Save className="w-5 h-5 mr-2" /> Salvar Configuracoes</>
          )}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
